import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  DialogContent,
  Dialog
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Router as RouterIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  ViewModule as CardsIcon,
  Router as VlansIcon,
  Settings as ProfilesIcon,
  DeviceHub as OnusIcon,
  Block as NotProvisionedIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import OLTForm from '../../components/forms/OLTForm';
import { oltService, OLT, ConnectionResult } from '../../services/oltService';
import {
  DashboardTab,
  CardsTab,
  VlansTab,
  ProfilesTab,
  PerfisOnuTab,
  OnusTab,
  NotProvisionedTab
} from './olt';

const OLTDashboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [olt, setOlt] = useState<OLT | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [oltFormDialog, setOltFormDialog] = useState<{
    open: boolean;
    olt: OLT | null;
  }>({
    open: false,
    olt: null
  });
  const [connectionTest, setConnectionTest] = useState<{
    testing: boolean;
    result: ConnectionResult | null;
  }>({
    testing: false,
    result: null
  });

  // Configuração das abas (removida a aba Portas PON)
  const tabs = [
    { label: 'Dashboard', icon: <DashboardIcon />, value: 0 },
    { label: 'Cards', icon: <CardsIcon />, value: 1 },
    { label: 'VLANs', icon: <VlansIcon />, value: 2 },
    { label: 'Profiles', icon: <ProfilesIcon />, value: 3 },
    { label: 'Perfis', icon: <ProfilesIcon />, value: 4 },
    { label: 'ONUs', icon: <OnusIcon />, value: 5 },
    { label: 'Não Provisionadas', icon: <NotProvisionedIcon />, value: 6 }
  ];

  useEffect(() => {
    if (id) {
      loadOLT(parseInt(id));
    }
  }, [id]);

  const loadOLT = async (oltId: number) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Carregando OLT ID:', oltId);
      const response = await oltService.getById(oltId);
      console.log('📡 Resposta do serviço:', response);
      
      // Verificar se a resposta é diretamente o objeto OLT (formato real do backend)
      if (response && typeof response === 'object' && 'id' in response && 'name' in response && 'vendor' in response) {
        console.log('✅ OLT carregada com sucesso (formato direto):', response);
        setOlt(response as unknown as OLT);
      } else {
        console.warn('⚠️ Resposta inválida:', response);
        throw new Error('OLT não encontrada');
      }
    } catch (err: any) {
      console.error('❌ Erro ao carregar OLT:', err);
      setError(err.message || 'Erro ao carregar dados da OLT');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!olt) return;

    setConnectionTest({ testing: true, result: null });

    try {
      const response = await oltService.testConnection(olt.id);
      setConnectionTest({
        testing: false,
        result: response.data
      });
      
      if (response.data.success) {
        toast.success('Conexão testada com sucesso!');
      } else {
        toast.error('Falha no teste de conexão');
      }
    } catch (err: any) {
      setConnectionTest({
        testing: false,
        result: {
          success: false,
          message: err.message || 'Erro ao testar conexão',
          error: err.message
        }
      });
      toast.error('Erro ao testar conexão');
    }
  };

  const handleEdit = (olt: OLT) => {
    setOltFormDialog({
      open: true,
      olt: olt
    });
  };

  const handleBack = () => {
    navigate('/ftth');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleRefresh = () => {
    if (olt) {
      loadOLT(olt.id);
    }
  };

  const handleCloseOLTForm = () => {
    setOltFormDialog({
      open: false,
      olt: null
    });
  };

  const handleOLTFormSuccess = (updatedOlt: OLT) => {
    handleCloseOLTForm();
    loadOLT(updatedOlt.id);
  };  

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !olt) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error || 'OLT não encontrada'}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Cabeçalho */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Voltar
          </Button>
          <Typography variant="h6" component="h1">
            <RouterIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            {olt?.name || 'Dashboard da OLT'}
          </Typography>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadOLT(olt?.id || 0)}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => handleEdit(olt)}
          >
            Editar
          </Button>
        </Box>
      </Box>

      {/* Abas */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
              scrollButtons="auto"
              aria-label="Abas de teste"
              sx={{
                minHeight: 40,
                '& .MuiTabs-scroller': {
                  display: 'flex',
                  justifyContent: 'flex-start'
                },
                '& .MuiTabs-flexContainer': {
                  justifyContent: 'flex-start'
                },
                '& .MuiTabs-indicator': {
                  display: 'none'
                },
                '& .MuiTab-root': {
                  minHeight: 40,
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                  padding: { xs: '6px 6px', sm: '6px 8px', md: '6px 12px' },
                  margin: '4px 2px 0 2px',
                  borderRadius: '8px 8px 0 0',
                  backgroundColor: 'transparent',
                  color: 'text.secondary',
                  transition: 'all 0.2s ease',
                  minWidth: { xs: 'auto', sm: 'auto', md: 'auto' },
                  maxWidth: { xs: '120px', sm: '140px', md: '160px' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    color: 'primary.main'
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    color: 'primary.main',
                    fontWeight: 600
                  }
                }
              }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              value={tab.value}
            />
          ))}
        </Tabs>
      </Paper>

            {/* Conteúdo das Abas */}
      <Box sx={{ mt: 2 }}>
        {activeTab === 0 && olt && (
          <DashboardTab
            olt={olt}
            connectionTest={connectionTest}
            onTestConnection={handleTestConnection}
            onRefresh={handleRefresh}
          />
        )}
        {activeTab === 1 && <CardsTab />}
        {activeTab === 2 && <VlansTab />}
        {activeTab === 3 && <ProfilesTab oltId={olt.id} />}
        {activeTab === 4 && <PerfisOnuTab oltId={olt.id} />}
        {activeTab === 5 && <OnusTab oltId={olt.id.toString()} />}
        {activeTab === 6 && <NotProvisionedTab oltId={olt.id.toString()} />}
      </Box>

      {/* Modal para Formulário OLT */}
      <Dialog
        open={oltFormDialog.open}
        onClose={handleCloseOLTForm}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 3 }}>
          <OLTForm
            olt={oltFormDialog.olt || undefined}
            onSuccess={handleOLTFormSuccess}
            onCancel={handleCloseOLTForm}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default OLTDashboard; 